import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { nanoid } from 'nanoid';
import { useLife } from './life';
import { useNotifications } from './notifications';
import { detectTopic, NPC_TOPIC_REPLIES, GENERIC_TOPIC_REPLIES } from './replyContext';
import type { DialogueChoice } from '@/content/dialogue/types';
import { applyChoiceEffects } from '@/engine/dialogueEffects';
import { pickResponseIdx, useDialogue } from './dialogue';

export interface EmailMessage {
  id: string;
  fromNpc: string;
  fromAddress: string;
  subject: string;
  body: string;            // simple text/markdown, rendered as paragraphs for MVP
  timestamp: number;
  read: boolean;
  starred: boolean;
}

interface EmailStore {
  emails: EmailMessage[];  // newest first
  selectedId: string | null;
  receive: (e: Omit<EmailMessage, 'id' | 'timestamp' | 'read' | 'starred'>) => void;
  open: (id: string) => void;
  close: () => void;
  toggleStar: (id: string) => void;
  remove: (id: string) => void;
  reply: (toId: string, body: string) => void;
  // Quick-reply: takes a DialogueChoice for the email's sender. Adds the player's
  // outgoing email then schedules a topical NPC reply from the authored tree.
  replyChoice: (toId: string, choice: DialogueChoice) => void;
}

const NOW = Date.now();

const SEEDS: EmailMessage[] = [
  {
    id: nanoid(),
    fromNpc: 'broker',
    fromAddress: 'compliance@robbingthehood.inc',
    subject: 'Welcome to RobbingTheHood Inc.',
    body: "Welcome aboard.\n\nBy opening this account, you waive: your right to be surprised when things go wrong, your right to a financial advisor who will return your calls, and any reasonable expectations.\n\nWe look forward to your ongoing engagement, particularly in the form of payment for order flow.\n\n— Compliance, RobbingTheHood Inc.",
    timestamp: NOW - 3 * 86_400_000,
    read: false,
    starred: false,
  },
  {
    id: nanoid(),
    fromNpc: 'spam',
    fromAddress: 'concierge@offshore-shells.biz',
    subject: 'You qualify for our Tycoon-tier package!',
    body: "Dear valued retail trader,\n\nMove your wealth offshore today! Our 3-step program ensures most clients never think about taxes again.*\n\n* You do not currently qualify. Bookmark us for when you do.\n\n— Offshore Shells International, a Nevis LLC of a Cayman SPV of a BVI trust",
    timestamp: NOW - 86_400_000,
    read: false,
    starred: false,
  },
  {
    id: nanoid(),
    fromNpc: 'mom',
    fromAddress: 'mom@aol.com',
    subject: 'Fwd: Fwd: Fwd: AMAZING NEW STOCK 🔥🔥🔥',
    body: "sweetie\n\nmy friend Janet sent this. she says her son made FORTY THOUSAND DOLLARS in a week. we should buy this $RUGS coin. let me know how to do it. should i mortgage the house?\n\nlove mom xoxo\n\np.s. dad says hi. did you eat today?",
    timestamp: NOW - 30 * 60_000,
    read: false,
    starred: false,
  },
  {
    id: nanoid(),
    fromNpc: 'sponsor',
    fromAddress: 'partnerships@brainjuice.energy',
    subject: 'Sponsorship Opportunity — BrainJuice Energy x You',
    body: "Hi @you,\n\nWe noticed your account performance and we love your vibe. We'd like to discuss a sponsorship — exclusive rights to mention BRAINJUICE in your Y bio for $250/month.\n\nPayments in BrainJuice.\n\nLet us know.\n\n— Tyler @ BrainJuice",
    timestamp: NOW - 8 * 3600_000,
    read: false,
    starred: false,
  },
  {
    id: nanoid(),
    fromNpc: 'lawyer',
    fromAddress: 'l.markowitz@markowitzandsons.law',
    subject: 'Re: Your inquiry',
    body: "Per our retainer:\n\n1. Stop posting about active positions on social media.\n2. Stop emailing the SEC. They are not your friend.\n3. Stop. Doing. The thing.\n\nWe will discuss further when you call back. My rate is $1,400/hour. The clock started when you opened this email.\n\n— Lou Markowitz, Esq.",
    timestamp: NOW - 2 * 3600_000,
    read: false,
    starred: false,
  },
  {
    id: nanoid(),
    fromNpc: 'dad',
    fromAddress: 'r.dad@aol.com',
    subject: 'fwd: fwd: fwd: a thing your mother sent',
    body: "son,\n\nyour mother forwarded me this. i'm forwarding it to you. i don't know what it is. neither does she. neither does the original sender, probably.\n\ndad",
    timestamp: NOW - 4 * 3600_000,
    read: false,
    starred: false,
  },
  {
    id: nanoid(),
    fromNpc: 'sister',
    fromAddress: 'amelia.s@gmail.com',
    subject: 'Save the date — wedding (the actual one)',
    body: "hey,\n\nformal save-the-date attached. ceremony at 4pm, reception at 6pm, dinner at 7pm. cocktail attire. plus one is included for wife.\n\nyou will, statistically, RSVP late. please don't this time.\n\n— amelia",
    timestamp: NOW - 6 * 3600_000,
    read: false,
    starred: false,
  },
];

export const useEmail = create<EmailStore>()(
  immer((set) => ({
    emails: SEEDS,
    selectedId: null,
    receive: (e) => set((s) => {
      s.emails.unshift({
        ...e,
        id: nanoid(),
        timestamp: Date.now(),
        read: false,
        starred: false,
      });
      if (s.emails.length > 200) s.emails.length = 200;
    }),
    open: (id) => {
      set((s) => {
        s.selectedId = id;
        const e = s.emails.find((x) => x.id === id);
        if (e) e.read = true;
      });
      // Side effect: opening the lawyer email actually triggers the retainer.
      const opened = useEmail.getState().emails.find((x) => x.id === id);
      if (opened?.fromAddress === 'l.markowitz@markowitzandsons.law' && useLife.getState().lawyerRetainerActiveSince === null) {
        useLife.getState().startLawyerRetainer();
        useNotifications.getState().push({
          surfaceId: 'email',
          severity: 'urgent',
          text: 'lawyer retainer ACTIVE — $1,400/h is now draining your cash',
        });
      }
    },
    close: () => set((s) => { s.selectedId = null; }),
    toggleStar: (id) => set((s) => {
      const e = s.emails.find((x) => x.id === id);
      if (e) e.starred = !e.starred;
    }),
    remove: (id) => set((s) => {
      s.emails = s.emails.filter((x) => x.id !== id);
      if (s.selectedId === id) s.selectedId = null;
    }),
    reply: (toId, body) => {
      const original = useEmail.getState().emails.find((e) => e.id === toId);
      if (!original) return;
      const trimmed = body.trim();
      if (!trimmed) return;
      const lc = trimmed.toLowerCase();
      const positive = /\b(thanks|love|please|sorry|sure|ok|will do|absolutely|yes|of course|appreciate|grateful)\b/.test(lc);
      const negative = /\b(no|fuck|stop|leave|never|hate|cease|sue|threaten|fired|done|enough)\b/.test(lc);

      type FU = { positive?: string[]; negative?: string[]; neutral?: string[] };
      const FALLBACK: FU = {
        neutral: [
          "Auto-reply: This inbox is monitored irregularly. Your message has been read by software.",
          "We've received your message. We will respond when convenient. For us.",
          "Thank you for reaching out. A reply may follow. Or may not.",
        ],
      };
      const followups: Record<string, FU> = {
        mom: {
          positive: [
            "sweetie that means so much.\n\nproud of you. genuinely.\n\nlove mom",
            "you wrote back!! and so quickly!! love love love.\n\nlove mom",
          ],
          negative: [
            "sweetie.\n\nokay.\n\nlove mom (still)",
            "i'll pretend i didn't see that. love mom.",
          ],
          neutral: [
            "sweetie. ok.\n\ndid you eat. please eat.\n\nlove mom",
            "got it. love you. dad says hi.",
          ],
        },
        broker: {
          positive: [
            "Acknowledged. Your engagement is appreciated, in the limited regulatory sense in which we are permitted to appreciate things.\n\n— Compliance, RobbingTheHood Inc.",
          ],
          negative: [
            "Cease-and-communicate noted.\n\nWe will continue to communicate, regardless. Per the terms of service, which you accepted, which neither of us read.\n\n— Compliance",
          ],
          neutral: [
            "Acknowledged.\n\nReceipt of your reply does not constitute action on our part. Or, frankly, attention.\n\n— Compliance, RobbingTheHood",
          ],
        },
        lawyer: {
          positive: [
            "You owe me \$1,400 for reading this.\n\nAnd \$1,400 for reading the next one.\n\n— Lou Markowitz, Esq.",
          ],
          negative: [
            "Your retainer is non-refundable. \$1,400/hour is non-refundable. Your reading speed is also non-refundable.\n\nMy refusal to be fired by email is, however, very refundable.\n\n— Lou",
          ],
          neutral: [
            "Acknowledged. \$1,400/h still ticking.\n\nDo not put anything in writing without me reviewing it first. This includes texts. This includes Y posts. This includes birthday cards.\n\n— Lou Markowitz, Esq.",
          ],
        },
        sponsor: {
          positive: [
            "Amazing! We'll send the BrainJuice coin to your wallet.\n\nSetup is straightforward: download our 47MB app, complete a 14-step KYC process, and send us \$200 in setup fees first. The coin will be airdropped within 90 days.\n\n— Tyler @ BrainJuice",
          ],
          negative: [
            "Noted. You'll regret this when BrainJuice IPOs.\n\nWhich it will. Soon. We've been close for a while.\n\n— Tyler",
          ],
          neutral: [
            "We'll follow up. Possibly. Probably not.\n\n— Tyler @ BrainJuice",
          ],
        },
        spam: {
          positive: [
            "AUTO-REPLY: Your reply has been added to our list! You will hear from us approximately every 14 minutes for the next 60 days.\n\n— Offshore Shells International",
            "Thank you for engaging! As a reward, we have signed you up for 4 additional newsletters and one webinar. Webinar attendance is mandatory.\n\n— Concierge",
          ],
          negative: [
            "AUTO-REPLY: We're sorry to see you reply with hostility. We will continue contacting you, more frequently, until your tone improves.\n\n— Customer Engagement",
            "Your reply suggests you may not understand the offer. Please find attached a 47-page brochure. We will resend monthly.\n\n— Tier-Concierge",
          ],
          neutral: [
            "AUTO-REPLY: Thank you. Your message has been received by a server in a country we will not specify.\n\n— Offshore Shells International, a Nevis LLC of a Cayman SPV of a BVI trust",
          ],
        },
        plaintiff: {
          positive: [
            "Counsel for the plaintiff is in receipt.\n\nYour cooperative tone has been entered as Exhibit B. It will be used in cross-examination.\n\n— Class Counsel",
          ],
          negative: [
            "Counsel for the plaintiff is in receipt.\n\nYour hostile tone has been entered as Exhibit A. We will be amending the complaint.\n\nDiscovery requests will follow shortly.\n\n— Class Counsel",
          ],
          neutral: [
            "Counsel for the plaintiff is in receipt.\n\nThis email has been added to the discovery file. Please preserve all communications relating to this matter.\n\n— Class Counsel",
          ],
        },
        irs: {
          positive: [
            "Your reply has been logged.\n\nNothing about this email reduces your liability. Thank you for using IRS Online.\n\n— Internal Revenue Service",
          ],
          negative: [
            "Your reply has been logged.\n\nThe Service does not respond to threats. The Service responds to filings, on time, with payment.\n\n— Internal Revenue Service",
          ],
          neutral: [
            "Your reply has been logged.\n\nFor faster service, please refer to publication 17, the entirety of which is mandatory reading.\n\n— Internal Revenue Service",
          ],
        },
        fan: {
          positive: [
            "OH MY GOD U REPLIED 😭😭😭\n\ngonna print this and frame it. mom is gonna lose it.",
            "❤️❤️❤️ u r my role model i tell ppl about u CONSTANTLY",
          ],
          negative: [
            "u dont have to be mean about it. i was just being nice.",
            "ok. i was a fan. unfan. unsubscribe. blocked. /sad",
          ],
          neutral: [
            "<3",
            "noted king. love u",
          ],
        },
        hater: {
          positive: [
            "this is a manipulation tactic. i see right through it.\n\nstill suing.\n\n— a hater",
          ],
          negative: [
            "see you in court.\n\nbringing receipts. bringing my dad. bringing snacks.\n\n— a hater",
          ],
          neutral: [
            "save it. screenshotting this for the group chat where we plan your downfall.\n\n— a hater",
          ],
        },
        podcast: {
          positive: [
            "Amazing. We'll send a contract.\n\nSlot is 14 weeks out. Sponsor read of BetterHelp / RaytheonSocks / a coin TBD.\n\n— Booking",
          ],
          negative: [
            "Understood. We'll book your competitor instead.\n\n— Booking",
          ],
          neutral: [
            "Ack. Will follow up when slots open. Possibly never.\n\n— Booking",
          ],
        },
      };

      // Topic-aware first: scan player text for SEC / margin / money / divorce
      // / etc and try to pull a topic-specific reply from the per-NPC pool.
      // Falls back to sentiment-based pool below if no topic match.
      let replyText: string | undefined;
      // dynamic import would loop; static import at top of file
      // (replyContext was added there)
      const topicMatch = detectTopic(trimmed);
      if (topicMatch) {
        const npcTopicPool = NPC_TOPIC_REPLIES[original.fromNpc]?.[topicMatch.topic];
        const genericTopicPool = GENERIC_TOPIC_REPLIES[topicMatch.topic];
        const tPool = (npcTopicPool && npcTopicPool.length > 0) ? npcTopicPool : genericTopicPool;
        if (tPool && tPool.length > 0) {
          replyText = tPool[Math.floor(Math.random() * tPool.length)];
        }
      }
      if (!replyText) {
        const pool = followups[original.fromNpc] ?? FALLBACK;
        const arr = (positive && pool.positive) ? pool.positive
                  : (negative && pool.negative) ? pool.negative
                  : (pool.neutral ?? pool.positive ?? pool.negative ?? FALLBACK.neutral!);
        replyText = arr[Math.floor(Math.random() * arr.length)];
      }
      if (!replyText) return;
      setTimeout(() => {
        set((s) => {
          s.emails.unshift({
            id: nanoid(),
            fromNpc: original.fromNpc,
            fromAddress: original.fromAddress,
            subject: `Re: ${original.subject}`,
            body: replyText,
            timestamp: Date.now(),
            read: false,
            starred: false,
          });
          if (s.emails.length > 200) s.emails.length = 200;
        });
        useNotifications.getState().push({
          surfaceId: 'email', severity: 'info',
          text: `${original.fromNpc} replied`,
        });
      }, 6_000 + Math.random() * 8_000);
    },

    replyChoice: (toId, choice) => {
      const original = useEmail.getState().emails.find((e) => e.id === toId);
      if (!original) return;
      // Append the player's outgoing email immediately. Subject = "Re: original subject".
      set((s) => {
        s.emails.unshift({
          id: nanoid(),
          fromNpc: 'self',
          fromAddress: 'you@nasdaq.one',
          subject: `Re: ${original.subject}`,
          body: choice.playerSays,
          timestamp: Date.now(),
          read: true,
          starred: false,
        });
        if (s.emails.length > 200) s.emails.length = 200;
      });
      // Apply choice-level effects immediately
      applyChoiceEffects(choice.effects);
      if (choice.oneShot) useDialogue.getState().recordOneShot(`email:${original.fromNpc}`, choice.id);
      // Pick a non-repeating response variant
      const dlg = useDialogue.getState();
      const fired = dlg.firedPairs[`email:${original.fromNpc}`] ?? [];
      const idx = pickResponseIdx(choice.id, choice.responses.length, fired);
      const resp = choice.responses[idx];
      if (!resp) return;
      dlg.recordFired(`email:${original.fromNpc}`, choice.id, idx);
      // Mark sender as pending so EmailView can show "[sender] is typing"
      dlg.setPendingReply(`email:${original.fromNpc}`, true);
      // Schedule NPC reply email
      setTimeout(() => {
        useDialogue.getState().setPendingReply(`email:${original.fromNpc}`, false);
        set((s) => {
          s.emails.unshift({
            id: nanoid(),
            fromNpc: original.fromNpc,
            fromAddress: original.fromAddress,
            subject: `Re: ${original.subject}`,
            body: resp.text,
            timestamp: Date.now(),
            read: false,
            starred: false,
          });
          if (s.emails.length > 200) s.emails.length = 200;
        });
        applyChoiceEffects(resp.effects);
        useNotifications.getState().push({
          surfaceId: 'email', severity: 'info',
          text: `${original.fromNpc} replied`,
        });
      }, 6_000 + Math.random() * 8_000);
    },
  })),
);
